<template>
  <section v-bind:class="[{
    fontLoaded,
    'voting-disabled': !votingEnabled || !isVoteable || voteLimitReached || isTimerEnded,
    'has-voted': votingComplete || view === 'correctAnswer',
    'show-results': (view === 'correctAnswer' && WEBResultsEnabled === true) || (isVoteable && votingComplete && WEBResultsEnabled === true) || (!isVoteable && WEBOnPollClosing === 'results'),
    'style-round': cornerStyle === 'round',
    'style-square': cornerStyle === 'square',
    'has-multi-select': showMultiVote
  }]">
    <transition-group appear name="option-list" tag="ul" @after-enter="onAfterEnter">
      <li
        @click="submitVote(option, $event)"
        :class="[`option-list swpoll-vote item${index + 1} showanswer`, {
          active: option.selected,
          selected: option.selected,
          correct: option.correct,
          disabled: !option.selected && selectedMaxOptions
        }]"
        :disabled="(!option.selected && selectedMaxOptions) || view === 'correctAnswer'"
        :style="{ borderColor: answerBackgroundColor }"
        :data-index="index"
        v-for="(option, index) in options" :key="option.id"
      >
        <div class="swbar">
          <div class="swbar-bar" :style="{ backgroundColor: answerBackgroundColor, width: `${option.displayPercent || 0}%` }"></div>
        </div>
        <p class="swperc" :style="{ color: answerTextColor }">
          <span class="swvalue">{{option.displayPercent || 0}}</span>%
        </p>
        <div v-if="showMultiVote" class="swpoll-check">
          <input type="checkbox">
          <svg viewBox="0 0 17 13" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
            <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round">
              <g id="Artboard" transform="translate(-118.000000, -138.000000)" stroke="#08E22C" stroke-width="2">
                <polyline id="Path" points="119.424798 143.943473 125.336893 149.804049 133.424798 139.804049"></polyline>
              </g>
            </g>
          </svg>
        </div>
        <div v-if="option.formattedImage" class="swimg">
          <img :src="option.formattedImage" alt="">
        </div>
        <div class="text">
          <p :style="{ color: answerTextColor }">{{option.text}}</p>
        </div>
      </li>
    </transition-group>
    <transition appear :name="'button-' + options.length">
      <VoteButton v-if="loaded && showMultiVote" />
    </transition>
    <transition appear name="next-button">
      <NextButton v-if="pollGroupModeActive && (votingComplete || WEBCanSkip || isTimerEnded)" />
    </transition>
    <transition appear name="story-button">
      <StoryButton v-if="votingComplete && WEBNextType === 'storyLink' && WEBResultsEnabled" />
    </transition>
  </section>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';

import VoteButton from './VoteButton';
import NextButton from './NextButton';
import StoryButton from './StoryButton';

export default {
  name: 'Options',
  components: {
    VoteButton,
    NextButton,
    StoryButton
  },
  data() {
    return {
      submitVote: () => {}
    };
  },
  computed: {
    ...mapState('poll', [
      'votingEnabled',
      'votingComplete',
      'options',
      'cornerStyle',
      'loaded',
      'fontLoaded',
      'WEBNextType',
      'WEBOnPollClosing',
      'WEBResultsEnabled',
      'isActive',
      'answerTextColor',
      'answerBackgroundColor',
      'WEBStoryLinkURL',
      'voteLimitReached',
      'WEBCanSkip',
      'WEBShowCorrectOption'
    ]),
    ...mapState('voter', [
      'view',
    ]),
    ...mapGetters('poll', [
      'isVoteable',
      'showMultiVote',
      'selectedMaxOptions',
      'captchaEnabled',
      'isTimerEnded'
    ]),
    ...mapGetters('pollGroup', [
      'pollGroupModeActive',
      'hasNextPoll',
      'leaderboardEnabled',
    ]),
    hasLeaderBoard() {
      return (this.pollGroupModeActive && (this.WEBNextType === 'leaderboard' && this.leaderboardEnabled));
    },
    displayNext() {
      return (
        this.votingComplete
        || this.isTimerEnded
      ) && (
        this.showMultiVote
        || this.showTimer
        || this.view === 'correctAnswer'
        || this.WEBShowCorrectOption
        || this.WEBResultsEnabled
      );
    }
  },
  watch: {
    votingComplete(complete) {
      if (complete && !this.displayNext) {
        if (this.pollGroupModeActive) {
          this.hasNextPoll ? this.nextPoll() : this.switchView('congratulations');
        } else if (this.nextLink) {
          window.location.href = this.nextLink;
        }
      }
    }
  },
  methods: {
    ...mapMutations('poll', [
      'toggleSelection',
      'setOptionActionText'
    ]),
    ...mapActions('poll', [
      'vote'
    ]),
    ...mapActions('voter', [
      'registerVote',
      'switchView'
    ]),
    ...mapActions('pollGroup', [
      'nextPoll'
    ]),
    throttle(func, limit) {
      var inThrottle;
      var lastFunc;
      var lastRan;
      return function() {
        const context = this;
        const args = arguments;
        if (!inThrottle) {
          func.apply(context, args);
          lastRan = Date.now();
          inThrottle = true;
        } else {
          clearTimeout(lastFunc);
          lastFunc = setTimeout(function() {
            if ((Date.now() - lastRan) >= limit) {
              func.apply(context, args);
              lastRan = Date.now();
            }
          }, limit - (Date.now() - lastRan));
        }
      };
    },
    async _submitVote(option, event) {
      event.preventDefault();
      if (this.view === 'correctAnswer') return;
      let recaptchaToken = null;

      if (this.captchaEnabled) {
        await this.$recaptchaLoaded();
        recaptchaToken = await this.$recaptcha('vote');
      }

      this.toggleSelection(option.id);
      !this.showMultiVote && this.vote({
        recaptchaToken,
        registerVote: this.registerVote
      });

      if (!this.showMultiVote && option.nextLink) {
        if (option.nextLinkType === 'textBlob') {
          this.setOptionActionText(option.nextLink);
        } else {
          window.location.href = `${process.env.VUE_APP_API_URL}${option.nextLink}`;
        }
      }
      if (this.WEBNextType === 'storyLink' && !this.WEBResultsEnabled && this.WEBStoryLinkURL) {
        window.location.href = `${process.env.VUE_APP_API_URL}${this.WEBStoryLinkURL}`;
      }
    },
    onAfterEnter(el) {
      if (+el.getAttribute('data-index') === this.options.length - 1) {
        this.$emit('on-options-loaded');
      }
    }
  },
  created() {
    this.submitVote = this.throttle(this._submitVote, 100);
  }
}
</script>

<style scoped lang="scss">
section {
  flex: 0 0 auto;
  margin-top: 24px;
  min-width: 100px;
  z-index: 100;
}

ul {
  padding: 0;
  list-style: none;
  margin: 0 -1px 0 0;
  @media only screen and (max-width: 650px) {
    & {
      margin: 0px 10px;
    }
  }
}

.has-multi-select {
  .swimg {
    margin-left: -2px !important;
  }
}

.swpoll-vote {
  display: flex;
  align-items: center;
  position: relative;
  color: var(--answer-color);
  border: 1px solid #fff;
  transition: transform 900ms cubic-bezier(0.230, 1.000, 0.320, 1.000),
              box-shadow 350ms cubic-bezier(0.230, 1.000, 0.320, 1.000);
  border-radius: var(--answer-radius);
  padding: var(--answer-padding-ver) var(--answer-padding-hor);
  text-decoration: none !important;
  margin-bottom: var(--answer-bottommargin);
  margin-right: 1px;
  transition: .3s ease-out;
  transform: scale(1) translate3d(0,0,0);
  opacity: 1;
  backdrop-filter: blur(4px);
  min-height: var(--answer-min-height);
  cursor: pointer;
  background-color: var(--answer-bg);

  &:hover {
    .swimg {
      transform: scale(1.1);
      transition-delay: 0s;
    }
  }

  &.disabled {
    pointer-events: none;
  }

  p {
    margin: 0;
  }

  .text {
    padding-right: 50px;
    flex: 1 1 auto;
    position: relative;
    z-index: 1;
    opacity: 0;

    p {
      font-size: var(--answer-fontsize);
      font-weight: var(--answer-fontweight);

      span {
        display: inline;

        &.animateLetter {
          animation: letterPop ease-in-out .35s;
          animation-iteration-count: 1;
          animation-fill-mode: forwards;
          position: relative;
        }
      }
    }
  }

  .swpoll-check {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    width: 24px;
    height: 24px;
    border: 1px solid rgba(255,255,255,.8);
    border-radius: 4px;
    position: relative;
    z-index: 2;
    flex: 0 0 auto;

    input {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
    }

    svg {
      width: 65%;
      height: auto;
      transition: all 500ms cubic-bezier(0.175, 0.885, 0.320, 1.275);
      opacity: 0;
      transform: scale(.2);

      g {
        stroke: var(--button-bg);
      }
    }
  }

  .swbar {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    border-radius: var(--answer-radius);
    overflow: hidden;
    transform: translate3d(0, 0, 0);
    transition: all 750ms cubic-bezier(0.22, 1, 0.36, 1);

    .swbar-bar {
      background-color: var(--result-bar-bg);
      border-radius: var(--answer-radius);
      position: absolute;
      left: 0;
      top: 0;
      width: 0;
      transition: all 750ms cubic-bezier(0.22, 1, 0.36, 1);
      height: 100%;
      opacity: 0;
      visibility: hidden;
    }
  }

  .swperc {
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0;
    transition: all 750ms cubic-bezier(0.22, 1, 0.36, 1);
    visibility: hidden;
    z-index: 1;
  }

  .swimg {
    width: var(--answer-img-width);
    height: var(--answer-img-height);
    border-radius: var(--answer-img-radius);
    overflow: hidden;
    margin: -2px;
    margin-left: -10px;
    margin-right: 14px;
    flex: 0 0 auto;
    transition: all 2500ms cubic-bezier(0.22, 1, 0.36, 1);
    transform: scale(1);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &:hover,
  &:active {
    color: var(--answer-color);
    box-shadow: inset 0 0 0 var(--answer-borderwidth) transparent, 0 0 0 3px var(--answer-bordercolor);
  }
  &.selected {
    color: var(--answer-active);
    box-shadow: none;

    &:focus {
      color: var(--answer-active);
    }

    .swbar {
      background-color: var(--answer-bg-active);
    }

    .swpoll-check {
      background-color: rgba(255,255,255,.95);
      svg {
        opacity: 1;
        transform: scale(1);
      }
    }
  }
  &:focus {
    color: var(--answer-color);
  }

  .active {
    transform: scale(1.03);
    background-color: var(--answer-bg-active);
    color: var(--answer-active);

    &:focus {
      color: var(--answer-active);
    }

    .swpoll-check {
      background-color: rgba(255, 255, 255, 0.95);
    }

    .swpoll-check svg {
      opacity: 1;
      -webkit-transform: scale(1);
              transform: scale(1);
    }
  }
}

.voting-disabled {
  .swpoll-vote {
    pointer-events: none;
  }
}

.style-square {

  .swpoll-vote {
    border-radius: 4px;

    .swbar {
      border-radius: 4px;
    }
  }
}

.has-voted {
  .correct {
    .swbar {
      background-color: var(--result-bar-correct-bgRGB);
      .swbar-bar {
        background-color: var(--result-bar-correct-bg);
      }
    }

    &.selected {
      .swbar {
        .swbar-bar {
          background-color: var(--result-bar-correct-bg);
        }
      }
    }
  }
}

.style-veev .has-voted .correct .swbar {
  background-color: transparent;
  .swbar-bar {
    background-color: var(--result-bar-correct-bg) !important;
  }
}

.show-results {

  .swperc {
    opacity: 1;
    visibility: visible;
  }

  .swbar {
    .swbar-bar {
      opacity: 1;
      visibility: visible;
    }
  }

  .selected {

    .swbar {
      background-color: rgba(var(--answer-bg-activeRGB), 0.7);

      .swbar-bar {
        background-color: var(--answer-bg-active);
        opacity: 1;
        visibility: visible;
      }
    }
  }

  .swpoll-vote {

    &:nth-child(1) {

      .swperc,
      .swbar,
      .swbar-bar {
        transition-delay: 300ms;
      }
    }

    &:nth-child(2) {

      .swperc,
      .swbar,
      .swbar-bar {
        transition-delay: 450ms;
      }
    }

    &:nth-child(3) {

      .swperc,
      .swbar,
      .swbar-bar {
        transition-delay: 600ms;
      }
    }

    &:nth-child(4) {

      .swperc,
      .swbar,
      .swbar-bar {
        transition-delay: 750ms;
      }
    }

    &:nth-child(5) {

      .swperc,
      .swbar,
      .swbar-bar {
        transition-delay: 900ms;
      }
    }
  }
}

.option-list-enter-active {
  transition: all 750ms cubic-bezier(0.22, 1, 0.36, 1);
}

.option-list-enter {
  opacity: 0;
  transform: scale(0.8);
}

.option-list-enter-to {
  opacity: 1;
  transform: scale(1);
}

@for $i from 0 through 10 {
  .option-list-enter-active:nth-child(#{$i}) {
    transition-delay: $i * 100ms + 600ms;
  }

  .button-#{$i}-enter-active {
    transition: all 750ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .button-#{$i}-enter {
    opacity: 0;
    transform: scale(0.8);
  }

  .button-#{$i}-enter-to {
    opacity: 1;
    transform: scale(1);
  }

  .button-#{$i}-enter-active {
    transition-delay: ($i+1) * 100ms + 600ms;
  }
}

.fontLoaded {
  .text {
    opacity: 1;
  }
  .swpoll-vote {
    &.disabled {
      opacity: 0.5;
    }
  }
}

.ismultiselect .swpoll-vote.active {
  -webkit-box-shadow: none;
          box-shadow: none;
  background-color: #fff;
  color: #222;
}

.ismultiselect.showresults .swpoll-vote.active {
  -webkit-box-shadow: none;
          box-shadow: none;
  background-color: rgba(var(--answer-bg-activeRGB), 0.7);
  color: var(--answer-active);
}

.hidepercentage .swpoll-votes.swpoll-votes-colorresults .active {
  background-color: var(--danger-color);
  color: var(--white);
  -webkit-animation: shake-horizontal 0.8s cubic-bezier(0.455, 0.03, 0.515, 0.955) both;
          animation: shake-horizontal 0.8s cubic-bezier(0.455, 0.03, 0.515, 0.955) both;
}

.hidepercentage .swpoll-votes.swpoll-votes-colorresults .swpoll-correct.active {
  -webkit-animation: none;
          animation: none;
}

.hidepercentage .swpoll-vote.active {
  -webkit-box-shadow: none;
          box-shadow: none;
  background-color: #fff;
  color: #222;
}

.showresults .swpoll-vote.active {
  color: var(--answer-active);
  background-color: rgba(var(--answer-bg-activeRGB), 0.7);
  color: var(--answer-active);
}

.showresults .swpoll-vote.active .swbar-bar {
  background-color: var(--answer-bg-active);
}

.showresults .swpoll-vote.active .swpoll-check {
  opacity: 1;
  -webkit-box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
}

.showresults .swpoll-vote.active.swpoll-correct {
  background-color: rgba(var(--result-bar-correct-bgRGB), 0.7);
  color: var(--white);
}

.showresults .swpoll-vote.active.swpoll-correct .swbar-bar {
  background-color: var(--result-bar-correct-bg);
}

.show-results .swpoll-vote {
  background-color: var(--answer-show-bg);
}

.isHorizontal {
  section {
    margin: 12px auto 0;
  }

  ul {
    display: flex;
        flex-wrap: wrap;
    -webkit-box-pack: center;
        -ms-flex-pack: center;
    justify-content: center;
  }

  .swpoll-vote {
    margin-right: 8px;
    margin-bottom: 8px;
    padding: var(--answer-hor-padding-ver) var(--answer-hor-padding-hor);
    min-height: var(--answer-hor-min-height);
    font-size: var(--answer-hor-fontsize);

    .text {
      p {
        font-size: var(--answer-hor-fontsize);
      }
    }
  }
}

.style-bbl,
.style-veev {

  section {
    margin-left: 175px;
    margin-right: 0;
    padding-left: 22px;

    @media screen and (max-width: 768px) {
      margin-left: -6px;
      padding-right: 22px;
    }

    ul {
      justify-content: initial;

      @media screen and (max-width: 768px) {
        display: block;
      }

      .swpoll-vote {
        width: 48%;

        @media screen and (max-width: 768px) {
          width: 100%;
          max-width: 100%;
          margin-right: 17px;
        }

        .text {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          padding-right: 0;

          p {
            position: relative;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }
  }

  .show-results {
    ul .swpoll-vote .text {
      padding-right: 40px;
    }
  }
}

.style-veev section ul .swpoll-vote .text {
  white-space: normal;
}

.style-covenant {
  .swpoll-vote {
    border: none;
    .text {
      padding-right: 0;
    }
    display: flex;
    flex-direction: column;
    flex: 1;
    text-align: center;
  }
  .swpoll-vote:hover,
  .swpoll-vote:active {
    transform: scale(1.05);
    box-shadow: none;
  }
  .show-results {
    .text p {
      top: -25px;
      position: relative;
      color: var(--text-color) !important;
    }
    .swpoll-vote {
      height: 16px;
      padding: 0;
      margin: 0 22px;
    }
    .swperc {
      display: none;
    }
  }
  section {
    max-width: 720px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px 35px;
    ul {
      width: 100%;
    }
  }
  &.isHorizontal section {
    margin-top: 0;
  }
  &.isHorizontal .show-results {
    margin-top: 30px;
  }
  .has-voted .correct {
    .swbar {
      background-color: transparent;
      .swbar-bar {
        background-color: var(--result-bar-correct-bg) !important;
      }
    }
  }
}
@media screen and (max-width: 768px) {
  .style-covenant {
    section {
      max-width: 550px;
      padding: 10px 15px;
    }
    .swpoll-vote {
      padding: 10px 10px;
    }
    .show-results {
      .swpoll-vote {
        height: 16px;
        padding: 0;
        margin: 0 20px;
      }
    }
    &.isHorizontal .show-results {
      margin-top: 25px;
    }
  }
}
@media only screen and (max-width: 567px) {
  .style-covenant {
    .swpoll-vote {
      padding: 5px 9px;
      .text p {
        font-size: 12px;
      }
    }
    section {
      padding: 10px 0;
    }
    .show-results .swpoll-vote {
      margin: 0 3px;
      .text p {
        top: -20px;
      }
    }
  }
}
</style>
